import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
  Wallet, Info, Plus, Shield, Coins, AlertTriangle,
  ArrowRight, RefreshCw, BarChart2, Lock, Settings, Rocket, Copy, Check, ExternalLink
} from 'lucide-react';
import TradingWalletList from '../components/tradingWallet/TradingWalletList';
import CreateTradingWalletModal from '../components/tradingWallet/CreateTradingWalletModal';
import InstantWalletTransfer from '../components/tradingWallet/InstantWalletTransfer';
import { useGRPCConnector } from '../utils/gRPCConnector';
import { getUserTradingWallets, TradingWallet } from '../services/tradingWalletService';
import WalletDashboard from '../components/WalletDashboard';
import { useWalletBalances } from '../services/walletBalanceService';
import { getBalance } from '../services/shyftService';
import { getUserProfile } from '../services/userProfileService';
import WalletTradeHistory from '../components/WalletTradeHistory';

// Add custom CSS animations
const customAnimations = `
  /* Section divider styles */
  .section-divider {
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(71, 190, 143, 0.3), transparent);
    margin: 2rem 0;
    position: relative;
  }
  
  .section-divider::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 50%;
    transform: translateX(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #47be8f;
    box-shadow: 0 0 8px rgba(71, 190, 143, 0.8);
  }
  
  /* Trading wallet section styling */
  .trading-wallet-container {
    position: relative;
    overflow: visible;
  }
  
  /* Improved scrollbar styles for the entire page */
  .wallets-container::-webkit-scrollbar,
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  .wallets-container::-webkit-scrollbar-track,
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(30, 40, 40, 0.2);
    border-radius: 3px;
  }
  
  .wallets-container::-webkit-scrollbar-thumb,
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(71, 190, 143, 0.3);
    border-radius: 3px;
    transition: all 0.2s;
  }
  
  .wallets-container::-webkit-scrollbar-thumb:hover,
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(71, 190, 143, 0.5);
  }
  
  /* Remove fade effect for horizontal scrolling, use clean edges */
  .horizontal-scroll-container {
    position: relative;
    overflow: visible !important;
    padding: 2px;
    margin: 2px;
  }
  
  .horizontal-scroll-container::before,
  .horizontal-scroll-container::after {
    display: none;
  }
`;

interface WalletsProps {
  walletAddress?: string;
  onShowConnectModal: () => void;
}

const Wallets: React.FC<WalletsProps> = ({ walletAddress, onShowConnectModal }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const { subscribeToAddress, unsubscribeFromAddress, isConnected } = useGRPCConnector();
  const { setWallet, refreshBalances } = useWalletBalances();
  const [transferSuccess, setTransferSuccess] = useState(false);
  
  // Add state for selected trading wallet
  const [selectedWallet, setSelectedWallet] = useState<TradingWallet | null>(null);
  
  // Add state for global transfer modal
  const [showGlobalTransferModal, setShowGlobalTransferModal] = useState(false);
  const [transferSourceWalletId, setTransferSourceWalletId] = useState<string | null>(null);
  
  // Add state for global manage modal
  const [showGlobalManageModal, setShowGlobalManageModal] = useState(false);
  const [manageWalletId, setManageWalletId] = useState<string | null>(null);
  
  // Add state to track recent transfers between trading wallets
  const [recentTransfer, setRecentTransfer] = useState(false);
  const fastUpdateCountRef = useRef(0);
  
  // Add refs for balance update intervals - only keep fast update interval
  const fastUpdateInterval = useRef<NodeJS.Timeout | null>(null);
  
  // Add state to track last balance fetch time
  const [lastBalanceFetchTime, setLastBalanceFetchTime] = useState(0);
  const balanceThrottleTime = 10000; // 10 seconds between balance updates (increased from 5s)
  
  // Add state for activeWalletPublicKey
  const [activeWalletPublicKey, setActiveWalletPublicKey] = useState<string | null>(null);
  
  // Trading wallet states
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  
  // Add function to get activeWalletPublicKey
  const getActiveWalletPublicKey = useCallback(async () => {
    if (!walletAddress) return null;
    
    try {
      // Get from localStorage first
      const storedActiveWallet = localStorage.getItem('activeWalletKey');
      if (storedActiveWallet) {
        setActiveWalletPublicKey(storedActiveWallet);
        return storedActiveWallet;
      }
      
      // Otherwise try to get from user profile
      const profile = await getUserProfile(walletAddress);
      if (profile && profile.active_trade_wallet) {
        setActiveWalletPublicKey(profile.active_trade_wallet);
        return profile.active_trade_wallet;
      }
    } catch (error) {
      console.error('Error getting active wallet address:', error);
    }
    return null;
  }, [walletAddress]);
  
  // Update the fetchAllTradingWalletBalances function to log what's triggering it
  const fetchAllTradingWalletBalances = useCallback(async (forceRefresh = false, triggerSource = "unknown") => {
    if (!walletAddress || tradingWallets.length === 0) return;
    
    // Apply throttling to prevent excessive updates
    const now = Date.now();
    if (!forceRefresh && now - lastBalanceFetchTime < balanceThrottleTime) {
      console.log(`[FETCH SKIPPED] ${triggerSource}: Skipping balance fetch - too soon since last fetch`);
      return; // Skip if we've fetched recently
    }
    
    // Update last fetch time
    setLastBalanceFetchTime(now);
    console.log(`[FETCH TRIGGERED] ${triggerSource}: Fetching balances for all trading wallets`, new Date().toLocaleTimeString());
    
    // Track success and failures
    let successCount = 0;
    let failureCount = 0;
    
    // Instead of using map with Promise.all (which can fail entirely if any request fails),
    // use a more resilient approach with a for loop to ensure each wallet is attempted
    for (const wallet of tradingWallets) {
      try {
        const lamports = await getBalance(wallet.public_key);
        
        // Always dispatch an update for any non-undefined value
        if (lamports !== undefined) {
          // Dispatch balance update event for SOL
          window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
            detail: {
              address: wallet.public_key,
              balance: lamports
            }
          }));
          
          successCount++;
        } else {
          failureCount++;
        }
      } catch (error) {
        console.error(`Error fetching balance for wallet ${wallet.public_key}:`, error);
        failureCount++;
      }
    }
    
    // Only retry failures if there were more failures than successes
    if (failureCount > successCount) {
      // Use a second loop to retry all wallets that might have failed
      // This ensures we get as many balances as possible
      setTimeout(async () => {
        for (const wallet of tradingWallets) {
          try {
            const lamports = await getBalance(wallet.public_key);
            
            // Only dispatch if we got a valid balance
            if (lamports >= 0) {
              // Dispatch balance update event
              window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                  address: wallet.public_key,
                  balance: lamports
                }
              }));
            }
          } catch (error) {
            // Silent fail on retry
          }
        }
      }, 1000);
    }
    
    return { successCount, failureCount };
  }, [tradingWallets, walletAddress, lastBalanceFetchTime, balanceThrottleTime]);
  
  // Update the startFastBalanceUpdates function to use the trigger source
  const startFastBalanceUpdates = useCallback(() => {
    // Clear any existing fast update interval
    if (fastUpdateInterval.current) {
      console.log(`Clearing previous fast update interval ${fastUpdateInterval.current}`);
      clearInterval(fastUpdateInterval.current);
      fastUpdateInterval.current = null;
    }
    
    // Set flag for recent transfer
    setRecentTransfer(true);
    // Reset the counter ref
    fastUpdateCountRef.current = 0;
    
    // Start new fast update interval - every 2 seconds for 10 seconds (5 updates total)
    const intervalId = setInterval(() => {
      // Use a safe version that doesn't rely on state closure
      const checkAndUpdateFastCount = () => {
        // Increment the counter using the ref
        fastUpdateCountRef.current += 1;
        
        console.log(`Fast update #${fastUpdateCountRef.current} of 5`);
        
        // Fetch balances - force refresh to bypass throttling
        fetchAllTradingWalletBalances(true, "fast-update-interval");
        
        // If we've reached 5 updates, stop the fast updates
        if (fastUpdateCountRef.current >= 5) {
          if (fastUpdateInterval.current) {
            console.log(`Fast update complete: Clearing interval ${fastUpdateInterval.current}`);
            clearInterval(fastUpdateInterval.current);
            fastUpdateInterval.current = null;
          }
          setRecentTransfer(false);
          fastUpdateCountRef.current = 0; // Reset to 0
        }
      };
      
      // Run the safe update function
      checkAndUpdateFastCount();
      
    }, 2000); // Every 2 seconds
    
    // Store the interval ID
    fastUpdateInterval.current = intervalId;
    console.log(`Created new fast update interval with ID ${intervalId}`);
    
    // Initial fetch immediately with force refresh
    fetchAllTradingWalletBalances(true, "fast-update-initial");
    
    return () => {
      if (fastUpdateInterval.current) {
        console.log(`Callback cleanup: Clearing fast interval ${fastUpdateInterval.current}`);
        clearInterval(fastUpdateInterval.current);
        fastUpdateInterval.current = null;
      }
    };
  }, [fetchAllTradingWalletBalances]);
  
  // Update the trading wallet loading effect to completely remove balance fetching
  useEffect(() => {
    if (walletAddress) {
      // First initiate a call to fetch active wallet
      getActiveWalletPublicKey();

      // Then load the wallets WITHOUT triggering balance fetches
      getUserTradingWallets(walletAddress)
        .then(wallets => {
          // Update wallets state immediately
          setTradingWallets(wallets);
          
          console.log(`Loaded ${wallets.length} trading wallets - NO automatic balance fetch`);
        })
        .catch(err => {
          console.error('Failed to load trading wallets:', err);
        });
    }
  }, [walletAddress, refreshTrigger, getActiveWalletPublicKey]);
  
  // Update the handle transfer success function to be explicit about the trigger source
  const handleTransferSuccess = () => {
    // Toggle the transferSuccess state
    setTransferSuccess(prev => !prev);
    
    // Start fast balance updates with a clear trigger source
    startFastBalanceUpdates();
  };

  // Restore handleGlobalTransferSuccess function
  const handleGlobalTransferSuccess = () => {
    setShowGlobalTransferModal(false);
    
    // Dispatch a custom event to trigger transaction update
    window.dispatchEvent(new CustomEvent('ekko:transaction_update'));
    
    // Start fast balance updates after a transfer
    startFastBalanceUpdates();
  };

  // Handle manage success globally
  const handleGlobalManageSuccess = () => {
    setShowGlobalManageModal(false);
    
    // Refresh wallet list after potential changes
    setRefreshTrigger(prev => prev + 1);
    
    // Use the event system for consistency instead of direct refresh
    window.dispatchEvent(new CustomEvent('ekko:transaction_update'));
  };

  // Update the useEffect that handles wallet subscriptions:
  useEffect(() => {
    // Keep track of all subscribed addresses for cleanup
    const subscribedAddresses: string[] = [];
    
    if (walletAddress) {
      
      // Set the wallet address in the wallet balances store
      setWallet(walletAddress);
      
      // Subscribe to the wallet address for balance updates with retry logic
      if (isConnected) {
        const subscribeWithRetry = async (address: string, retries = 3) => {
          try {
            await subscribeToAddress(address);
            subscribedAddresses.push(address);
          } catch (err) {
            if (retries > 0) {
              console.warn(`Subscription retry (${retries}) for address:`, address);
              setTimeout(() => subscribeWithRetry(address, retries - 1), 1000);
            } else {
              console.error('Failed to subscribe to wallet address after retries:', err);
            }
          }
        };
        
        subscribeWithRetry(walletAddress);
        
        // Load and subscribe to trading wallets with improved error handling
        getUserTradingWallets(walletAddress)
          .then(tradingWallets => {
            for (const wallet of tradingWallets) {
              subscribeWithRetry(wallet.public_key);
            }
          })
          .catch(err => console.error('Failed to load trading wallets for subscription:', err));
      }
    } else {
      // Clear wallet when disconnected
      setWallet(null);
    }
    
    // Clear selected wallet when wallet address changes
    setSelectedWallet(null);
    
    // Handler for transfer completion - refresh the wallet list
    const handleTransferCompletion = (event: CustomEvent<{ 
      fromWallet: string, 
      toWallet: string, 
      amount: number, 
      tokenMint: string, 
      transferType: string 
    }>) => {
      console.log('🔄 Transfer completed, refreshing wallet list...');
      
      // Force refresh the wallet list
      setRefreshTrigger(prev => prev + 1);
      
      // Also trigger balance updates for the specific wallets
      const { fromWallet, toWallet } = event.detail;
      
      if (fromWallet) {
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
          detail: { walletPublicKey: fromWallet }
        }));
      }
      
      if (toWallet && toWallet !== fromWallet && !toWallet.includes('external')) {
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
          detail: { walletPublicKey: toWallet }
        }));
      }
    };
    
    // Listen for transfer completion events
    window.addEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    
    // Cleanup function
    return () => {
      for (const address of subscribedAddresses) {
        unsubscribeFromAddress(address);
      }
      
      // Remove transfer completion listener
      window.removeEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    };
  }, [walletAddress, isConnected, setWallet]);
  
  useEffect(() => {
    if (walletAddress) {
      // Reset all state when wallet address changes
      getActiveWalletPublicKey();
    }
  }, [walletAddress, getActiveWalletPublicKey]);

  // Add a manual refresh function for the refresh button
  const handleManualRefresh = () => {
    // Force refresh all wallets when user manually refreshes
    fetchAllTradingWalletBalances(true, "user-manual-refresh");
  };

  // Function to handle wallet selection
  const handleWalletSelect = (wallet: TradingWallet | null) => {
    setSelectedWallet(wallet);
  };

  // Function to handle transfer button click from a wallet card
  const handleWalletTransferClick = (walletPublicKey: string) => {
    setTransferSourceWalletId(walletPublicKey);
    setShowGlobalTransferModal(true);
  };

  // Function to handle manage button click from a wallet card
  const handleWalletManageClick = (walletPublicKey: string) => {
    setManageWalletId(walletPublicKey);
    setShowGlobalManageModal(true);
  };

  const formatWalletAddress = (address: string | undefined) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const copyWalletAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  const handleWalletCreated = async () => {
    // Only refresh the wallet list without triggering balance updates
    setRefreshTrigger(prev => prev + 1);
    
    // We still need to subscribe the new wallet to gRPC updates
    if (walletAddress && isConnected) {
      try {
        // Get all trading wallets for the connected wallet
        const tradingWallets = await getUserTradingWallets(walletAddress);
        
        // Subscribe to each trading wallet for balance updates
        for (const wallet of tradingWallets) {
          try {
            await subscribeToAddress(wallet.public_key);
          } catch (error) {
            console.error(`Error subscribing to trading wallet ${wallet.public_key}:`, error);
          }
        }
      } catch (error) {
        console.error("Error subscribing to trading wallets after creation:", error);
      }
    }
  };

  const formatTime = (timestamp: number) => {
    // Convert Solana blockTime (in seconds) to JavaScript Date (in milliseconds)
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  useEffect(() => {
    getActiveWalletPublicKey();
  }, [getActiveWalletPublicKey]);

  // Keep component unmount cleanup just for the fast update interval
  useEffect(() => {
    // Clean up fast update interval on component unmount
    return () => {
      if (fastUpdateInterval.current) {
        console.log(`Component unmount: Clearing fast update interval ${fastUpdateInterval.current}`);
        clearInterval(fastUpdateInterval.current);
        fastUpdateInterval.current = null;
      }
    };
  }, []); // Empty dependency array = run only on component mount/unmount

  return (
    <div className="h-[calc(100vh-3.5rem)] w-full wallets-container relative">
      {/* Custom Animations */}
      <style>{customAnimations}</style>
      
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#47be8f]/5 via-transparent to-transparent z-[-1]"></div>
      
      <div className="flex h-full">
        {/* Left Sidebar - Simplified */}
        <div className="backdrop-blur-sm bg-dark-grey/40 border-r border-gray-700/30 w-56 py-3 px-2.5 flex flex-col">
          <div className="flex items-center gap-2 mb-4 mt-2 px-1.5">
            <div className="p-1.5 rounded-lg bg-[#47be8f]/20 text-[#47be8f]">
              <Wallet className="w-4 h-4" />
            </div>
            <h1 className="text-base font-bold text-white/90">Wallet Hub</h1>
          </div>
          
          {/* Connected Wallet Status */}
          <div className="mb-3 py-2 px-2.5 rounded-md backdrop-blur-sm bg-dark-grey/30 border border-gray-700/20">
            {walletAddress ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-300">{formatWalletAddress(walletAddress)}</span>
                </div>
                <button 
                  onClick={copyWalletAddress}
                  className="text-gray-400 hover:text-white transition-colors duration-200" 
                  title="Copy Address"
                >
                  {copiedAddress ? (
                    <Check size={12} className="text-green-400" />
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={onShowConnectModal}
                className="flex items-center justify-center w-full gap-1.5 text-xs text-[#47be8f] hover:text-[#47be8f]/80 transition-colors duration-200"
              >
                <Lock size={12} />
                <span>Connect Wallet</span>
              </button>
            )}
          </div>
          
          {/* Beta Notice - moved up in sidebar for visibility */}
          <div className="mb-3 py-2 px-2.5 text-xs rounded-md backdrop-blur-sm bg-[#47be8f]/10 border border-[#47be8f]/20 z-10 relative">
            <div className="flex items-start gap-2">
              <div className="p-1 rounded-full bg-[#47be8f]/20 text-[#47be8f] mt-0.5 flex-shrink-0">
                <AlertTriangle size={10} />
              </div>
              <div>
                <h3 className="font-medium mb-0.5 text-[#47be8f]">Trading Wallet Benefits</h3>
                <p className="text-gray-400 text-[11px]">
                  Trading wallets let you isolate funds for trading, set risk budgets, and trade without signing every transaction.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Area - Full width layout */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {walletAddress ? (
            <div className="p-5">
              {/* Two-column layout container */}
              <div className="flex flex-col md:flex-row space-y-8 md:space-y-0 md:space-x-6">
                {/* Left Column - Trading Wallets */}
                <div className="md:w-3/5">
                  {/* Trading Wallets Section */}
                  <div className="trading-wallet-container overflow-visible" id="trading-wallet-section">
                {/* Header Section */}
                <div className="flex flex-wrap items-center justify-between mb-5 gap-3">
                  <div>
                    <h1 className="text-xl font-bold mb-1 flex items-center gap-2 text-white/90">
                      <div className="w-2 h-2 rounded-full bg-[#47be8f]"></div>
                      Trading Wallets
                    </h1>
                    <p className="text-gray-400 text-sm">
                      Create dedicated wallets for trading without signing every transaction
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => setShowCreateModal(true)}
                    className="py-1.5 px-3 rounded-md text-sm flex items-center gap-1.5 backdrop-blur-sm bg-yellow-500/80 text-black font-medium hover:bg-yellow-500 transition-all duration-200"
                    id="create-wallet-button"
                  >
                    <Plus size={14} />
                    <span>Create Wallet</span>
                  </button>
                </div>
                
                    {/* Wallet List - displayed horizontally with full width and clean edges */}
                    <div id="trading-wallet-list" className="overflow-visible w-full">
                      <TradingWalletList 
                        walletAddress={walletAddress}
                        onCreateWallet={() => {
                          setShowCreateModal(true);
                        }}
                        refreshTrigger={refreshTrigger}
                        onSelectWallet={handleWalletSelect}
                        onTransferClick={handleWalletTransferClick}
                        onManageClick={handleWalletManageClick}
                        selectedWalletPublicKey={selectedWallet?.public_key}
                        activeWalletPublicKey={activeWalletPublicKey || undefined}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Transactions and Analytics */}
                <div className="md:w-2/5 flex flex-col h-full">
                  {/* Transaction History Section - fixed height */}
                  <div id="transaction-history-section" className="mb-0 flex flex-col h-[calc(100vh-180px)]">
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <div>
                        <h1 className="text-xl font-bold mb-1 flex items-center gap-2 text-white/90">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          {selectedWallet ? `${selectedWallet.name} Transactions` : 'Recent Transactions'}
                        </h1>
                        <p className="text-gray-400 text-sm">
                          {selectedWallet 
                            ? `Transaction history for ${formatWalletAddress(selectedWallet.public_key)}`
                            : 'Your transaction history and activities'
                          }
                        </p>
                      </div>
                    </div>
                    
                    {/* Enhanced Transaction History with Solana Tracker Data */}
                    <WalletTradeHistory 
                      walletAddress={selectedWallet ? selectedWallet.public_key : walletAddress || ''}
                      maxHeight="calc(100vh - 240px)"
                      onRefresh={handleManualRefresh}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm mx-auto">
                <div className="p-6 rounded-lg backdrop-blur-sm bg-dark-grey/40 border border-gray-700/30 wallet-card-glow">
                  <div className="p-3 rounded-full bg-[#47be8f]/20 text-[#47be8f] mx-auto w-fit mb-4">
                    <Lock size={24} />
                  </div>
                  <h2 className="text-xl font-bold mb-3 text-white/90">Connect Your Wallet</h2>
                  <p className="text-sm text-gray-400 mb-5">
                    Please connect a wallet to manage your trading wallets
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      onShowConnectModal();
                    }}
                    className="py-2 px-5 rounded-md text-sm backdrop-blur-sm bg-yellow-500/80 text-black font-medium hover:bg-yellow-500 transition-all duration-200"
                  >
                    Connect Wallet
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <CreateTradingWalletModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
        }}
        onWalletCreated={handleWalletCreated}
        connectedWalletAddress={walletAddress}
      />
      
      {/* Global Transfer Modal */}
      {showGlobalTransferModal && transferSourceWalletId && (
        <InstantWalletTransfer
          wallets={tradingWallets}
          userWalletAddress={walletAddress || ''}
          sourceWalletId={transferSourceWalletId}
          onClose={() => setShowGlobalTransferModal(false)}
          onSuccess={handleGlobalTransferSuccess}
          onWalletUpdate={(wallet) => {
            // Update trading wallet in local state if needed
            setTradingWallets(prev => 
              prev.map(w => w.public_key === wallet.public_key ? wallet : w)
            );
          }}
        />
      )}
      
      {/* Global Manage Modal */}
      {showGlobalManageModal && manageWalletId && (
        <InstantWalletTransfer
          wallets={tradingWallets}
          userWalletAddress={walletAddress || ''}
          sourceWalletId={manageWalletId}
          onClose={() => setShowGlobalManageModal(false)}
          onSuccess={handleGlobalManageSuccess}
          onWalletUpdate={(wallet) => {
            // Update trading wallet in local state if needed
            setTradingWallets(prev => 
              prev.map(w => w.public_key === wallet.public_key ? wallet : w)
            );
          }}
          initialTab="manage"
        />
      )}
    </div>
  );
};

export default Wallets;